import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Wallet as WalletIcon, Plus, RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import PayoutPanel from "./PayoutPanel";

/**
 * Painel da Carteira da Empresa: saldo, recargas pendentes, histórico, abastecer via MP.
 */
export default function CompanyWallet({ company }) {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/company-wallet/${company.id}`);
      setData(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [company.id]);

  const recharge = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Informe um valor válido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/company-wallet/${company.id}/recharge`, { amount: parseFloat(amount) });
      const url = data.init_point || data.sandbox_init_point;
      if (url) {
        window.open(url, "_blank");
        toast.success("Abrindo Mercado Pago. Após pagar, clique em 'Confirmar pagamento'.");
      }
      setAmount("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmPayment = async (rechargeId) => {
    setConfirming(rechargeId);
    try {
      const { data } = await api.post(`/company-wallet/${company.id}/confirm/${rechargeId}`);
      if (data.ok && data.status === "confirmed") {
        toast.success(`Recarga confirmada! +B$ ${data.amount.toFixed(2)} no saldo.`);
      } else if (data.ok && data.status === "already_confirmed") {
        toast.info("Esta recarga já estava confirmada.");
      } else {
        toast.warning(data.message || "Pagamento ainda não aprovado no Mercado Pago.");
      }
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setConfirming(null); }
  };

  if (!data) return <Card className="p-5 rounded-2xl border-stone-200"><p className="text-sm text-stone-500">Carregando carteira...</p></Card>;

  return (
    <div className="space-y-4" data-testid={`company-wallet-${company.id}`}>
      {/* 3 wallets — fully explicit */}
      <div className="grid sm:grid-cols-3 gap-3">
        {[
          { key: "main", grad: "from-blue-900 to-blue-950", ring: "blue-200" },
          { key: "marketing", grad: "from-amber-700 to-orange-800", ring: "amber-200" },
          { key: "voucher", grad: "from-purple-800 to-fuchsia-900", ring: "purple-200" },
        ].map(({ key, grad, ring }) => {
          const w = data.wallets?.[key] || { balance: 0, label: key, description: "" };
          return (
            <Card key={key} className={`p-4 rounded-2xl bg-gradient-to-br ${grad} text-white shadow-lg border-0`} data-testid={`wallet-${key}-${company.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">{w.icon || "💼"}</span>
                <span className={`text-[10px] uppercase tracking-widest text-${ring} font-semibold`}>{w.label}</span>
              </div>
              <div className="font-display text-3xl font-bold tracking-tight" data-testid={`wallet-${key}-balance`}>
                B$ {w.balance.toFixed(2).replace(".", ",")}
              </div>
              <p className="text-[11px] opacity-80 mt-2 leading-tight">{w.description}</p>
            </Card>
          );
        })}
      </div>

      {/* Recharge form (goes to Marketing wallet) */}
      <Card className="p-5 rounded-2xl border-2 border-amber-200 bg-amber-50/40">
        <h4 className="font-display font-bold text-amber-900 mb-1 flex items-center gap-2">
          <Plus size={16}/> Recarregar Carteira de Marketing
        </h4>
        <p className="text-xs text-amber-800 mb-3">Adicione saldo para distribuir cashback (padrão + turbo) aos seus clientes. Pode também pagar via Lojinha de Pacotes com bônus.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Valor (R$)</Label>
            <Input type="number" step="0.01" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="100.00" className="rounded-xl mt-1" data-testid="cw-amount"/>
          </div>
          <Button disabled={busy || !amount} onClick={recharge} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold" data-testid="cw-recharge-btn">
            <Plus size={16} className="mr-1"/> {busy ? "Gerando..." : "Recarregar Mercado Pago"}
          </Button>
          <a href="/merchant/packages" className="text-xs font-bold text-blue-900 hover:underline" data-testid="cw-shop-link">
            ou compre Pacote com bônus →
          </a>
        </div>
      </Card>

      {data.pending_recharges.length > 0 && (
        <Card className="p-5 rounded-2xl border-2 border-amber-300 bg-amber-50">
          <h4 className="font-display font-semibold text-amber-900 flex items-center gap-2 mb-3"><Clock size={16}/> Recargas pendentes ({data.pending_recharges.length})</h4>
          <div className="space-y-2">
            {data.pending_recharges.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-xl p-3" data-testid={`pending-recharge-${r.id}`}>
                <div>
                  <div className="text-sm font-semibold text-stone-900">R$ {r.amount.toFixed(2)}</div>
                  <div className="text-[11px] text-stone-500">{new Date(r.created_at).toLocaleString("pt-BR")} · ref: {r.id.slice(0,8)}</div>
                </div>
                <div className="flex gap-2">
                  {(r.init_point || r.sandbox_init_point) && (
                    <Button size="sm" variant="outline" onClick={() => window.open(r.init_point || r.sandbox_init_point, "_blank")} className="rounded-lg text-xs">
                      Pagar
                    </Button>
                  )}
                  <Button size="sm" onClick={() => confirmPayment(r.id)} disabled={confirming === r.id}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs" data-testid={`confirm-recharge-${r.id}`}>
                    {confirming === r.id ? <RefreshCw size={13} className="animate-spin"/> : <CheckCircle2 size={13} className="mr-1"/>}
                    Confirmar pagamento
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <PayoutPanel company={company} onRefresh={refresh}/>


      <Card className="p-5 rounded-2xl border-stone-200">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="font-display font-semibold text-stone-900">Extrato da carteira ({data.transactions.length})</h4>
          <button onClick={refresh} className="text-xs text-blue-900 hover:underline flex items-center gap-1"><RefreshCw size={12}/> Atualizar</button>
        </div>
        <div className="p-3 mb-3 rounded-xl bg-stone-50 border border-stone-200 text-[11px] text-stone-600 space-y-1">
          <p><strong>📖 Legenda:</strong></p>
          <p>🟢 <strong>Entradas</strong>: recargas via PIX/cartão (compra de pacote), vendas recebidas, ajustes do admin.</p>
          <p>🔴 <strong>Saídas</strong>: cashback distribuído aos clientes, taxas, saques PIX, compra de vouchers.</p>
          <p>Cada movimento mostra a descrição completa, data e referência da operação.</p>
        </div>
        <div className="divide-y divide-stone-100 max-h-96 overflow-auto">
          {data.transactions.length === 0 && <p className="text-sm text-stone-500 py-4">Nenhuma movimentação ainda.</p>}
          {data.transactions.map((t) => {
            const info = describeTx(t);
            const isIn = t.amount >= 0;
            return (
              <div key={t.id} className="py-3 flex items-start justify-between gap-3" data-testid={`cw-tx-${t.id}`}>
                <div className="flex items-start gap-2.5 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${info.bgClass}`}>
                    <span className="text-base">{info.icon}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-stone-500">{info.label}</div>
                    <div className="text-sm text-stone-900 truncate">{t.description}</div>
                    <div className="text-[10px] text-stone-400 mt-0.5">{new Date(t.created_at).toLocaleString("pt-BR")} · #{t.id.slice(0,8)}</div>
                  </div>
                </div>
                <div className={`font-display font-bold text-base whitespace-nowrap ${isIn ? "text-emerald-700" : "text-red-700"}`}>
                  {isIn ? "+" : ""}B$ {t.amount.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// Map raw tx type/category to user-friendly label, icon, color
function describeTx(t) {
  const type = (t.type || "").toUpperCase();
  const cat = (t.category || "").toLowerCase();
  // Saídas (cashback distribuído)
  if (type === "CASHBACK_ISSUE")
    return { label: "Cashback ao cliente", icon: "🎁", bgClass: "bg-rose-50" };
  if (type === "PAYOUT" || cat.includes("payout") || cat.includes("saque"))
    return { label: "Saque PIX", icon: "🏦", bgClass: "bg-orange-50" };
  if (cat === "voucher_package_via_bemcash")
    return { label: "Compra de Voucher", icon: "🎟️", bgClass: "bg-purple-50" };
  if (type === "ADJUSTMENT" && t.amount < 0)
    return { label: "Ajuste do Admin (saída)", icon: "📝", bgClass: "bg-stone-100" };
  // Entradas
  if (cat === "package_cashback" || cat === "voucher_credit")
    return { label: "Recarga de pacote", icon: "💳", bgClass: "bg-emerald-50" };
  if (cat.includes("order") || cat.includes("venda"))
    return { label: "Venda recebida", icon: "💰", bgClass: "bg-emerald-50" };
  if (type === "ADJUSTMENT" && t.amount >= 0)
    return { label: "Ajuste do Admin (entrada)", icon: "📝", bgClass: "bg-stone-100" };
  return { label: type || "Movimentação", icon: t.amount >= 0 ? "⬆️" : "⬇️", bgClass: "bg-stone-100" };
}
